/**
 * Interaction layer for transcribed design screens.
 *
 * The design source is static markup: navigation and calls to action are plain `<span>`
 * and `<button>` elements with no handlers. Rather than edit that markup — which is the
 * thing we are trying to reproduce exactly — this delegates from the root and maps an
 * element's own text to a route. The DOM stays byte-for-byte the design's; the behaviour
 * is layered on top.
 *
 * Anything not in the map is inert, exactly as it is in the design.
 */

import { useCallback, useEffect, useRef, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

/** Text on the element → where it goes. Matched case-insensitively on trimmed text. */
export type RouteMap = Record<string, string>;

const CLICKABLE = 'button, a, [role="button"], span, div';

export function Interactive({
  routes,
  children,
  label,
}: {
  routes: RouteMap;
  children: ReactNode;
  label?: string;
}) {
  const navigate = useNavigate();
  const root = useRef<HTMLDivElement>(null);

  const resolve = useCallback(
    (target: EventTarget | null): string | null => {
      let el = target as HTMLElement | null;
      // Walk up: the click usually lands on a text node's parent, not the button.
      while (el && el !== root.current) {
        if (el.matches?.(CLICKABLE)) {
          const text = (el.textContent ?? '').trim().toLowerCase();
          const to = routes[text];
          if (to) return to;
        }
        el = el.parentElement;
      }
      return null;
    },
    [routes],
  );

  /*
   * Make the mapped elements real controls: focusable, announced as links, and
   * operable by keyboard. Done after mount so the transcription stays untouched.
   */
  useEffect(() => {
    const node = root.current;
    if (!node) return;
    const wanted = new Set(Object.keys(routes));
    for (const el of node.querySelectorAll<HTMLElement>(CLICKABLE)) {
      const text = (el.textContent ?? '').trim().toLowerCase();
      if (!wanted.has(text)) continue;
      // Only the innermost element carrying that exact text.
      if (el.querySelector(CLICKABLE)) continue;
      if (el.tagName !== 'BUTTON' && el.tagName !== 'A') {
        el.setAttribute('role', 'link');
        el.tabIndex = 0;
      }
      el.style.cursor = 'pointer';
    }
  }, [routes]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const to = resolve(e.target);
    if (!to) return;
    e.preventDefault();
    navigate(to);
  };

  return (
    <div
      ref={root}
      aria-label={label}
      onClick={(e) => {
        const to = resolve(e.target);
        if (!to) return;
        e.preventDefault();
        navigate(to);
      }}
      onKeyDown={onKeyDown}
    >
      {children}
    </div>
  );
}
